import type { Element } from "@fusioncraft/shared";
import { tintStyle } from "../emojiTint";
import { forwardRef, useMemo, useState, type PointerEvent as ReactPointerEvent } from "react";

type Sort = "time" | "name";

interface Props {
  elements: Element[];
  onPick: (el: Element) => void;
  onDragStart: (e: ReactPointerEvent, el: Element) => void;
}

export const Ledger = forwardRef<HTMLElement, Props>(function Ledger({ elements, onPick, onDragStart }, ref) {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<Sort>("time");
  const [ascending, setAscending] = useState(true);
  const [discoveriesOnly, setDiscoveriesOnly] = useState(false);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = elements.filter((e) => (!discoveriesOnly || e.firstDiscovery) && (!q || e.name.toLowerCase().includes(q)));
    if (sort === "name") list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    if (!ascending) list = [...list].reverse();
    return list;
  }, [elements, query, sort, ascending, discoveriesOnly]);

  return (
    <aside className="sidebar" ref={ref} aria-label="Items">
      <div className="items">
        <div className="sidebar-title">
          {discoveriesOnly ? "Discoveries" : "Items"} <span>{shown.length}</span>
        </div>
        <div className="items-inner">
          {shown.map((el) => {
            const tint = tintStyle(el.emoji);
            return (
              <button
                key={el.id}
                className={tint ? "item tinted" : "item"}
                style={tint}
                onPointerDown={(e) => onDragStart(e, el)}
                onClick={() => onPick(el)}
              >
                <span className="item-emoji">{el.emoji}</span> {el.name}
              </button>
            );
          })}
        </div>
        {shown.length === 0 && (
          <p className="sidebar-empty">
            {query ? `No items match “${query}”.` : discoveriesOnly ? "No first discoveries yet." : "No items yet."}
          </p>
        )}
        {/* Only while starting out: once something has been crafted the hint is just clutter. */}
        {!query && !discoveriesOnly && elements.length <= 4 && (
          <div className="instruction">
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <path d="M14 8H2.5M7 3.5 2.5 8 7 12.5" />
            </svg>
            Drag elements to craft
          </div>
        )}
      </div>

      <div className="sidebar-controls">
        <div className="sidebar-sorting">
          <button
            className={discoveriesOnly ? "sorting-item is-active" : "sorting-item"}
            onClick={() => setDiscoveriesOnly((d) => !d)}
          >
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <path d="M8 1.5l1.6 4.2 4.4.3-3.4 2.8 1.1 4.3L8 10.7l-3.7 2.4 1.1-4.3L2 6l4.4-.3z" />
            </svg>
            Discoveries
          </button>
          <button className="sorting-item" onClick={() => setSort((s) => (s === "time" ? "name" : "time"))}>
            <svg viewBox="0 0 16 16" aria-hidden="true">
              {sort === "time" ? (
                <>
                  <circle cx="8" cy="8" r="6" />
                  <path d="M8 4.5V8l2.5 1.5" />
                </>
              ) : (
                <path d="M3 12.5 5.5 4h1l2.5 8.5M4 10h4M10.5 4.5h3l-3 7h3" />
              )}
            </svg>
            {sort === "time" ? "Time" : "Name"}
          </button>
          <button
            className="sorting-item"
            onClick={() => setAscending((a) => !a)}
            aria-label={ascending ? "Ascending" : "Descending"}
          >
            <svg viewBox="0 0 16 16" aria-hidden="true" style={{ transform: ascending ? undefined : "rotate(180deg)" }}>
              <path d="M8 2.5v11M4 9.5l4 4 4-4" />
            </svg>
          </button>
        </div>
        <div className="sidebar-search">
          <svg viewBox="0 0 20 20" aria-hidden="true">
            <circle cx="8.5" cy="8.5" r="5.5" />
            <path d="M12.5 12.5 17 17" />
          </svg>
          <input
            className="sidebar-input"
            type="search"
            placeholder={`Search (${elements.length} items)...`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search items"
          />
        </div>
      </div>
    </aside>
  );
});
